import { computed, Injectable, signal } from '@angular/core';

/** Wait this long before showing; most calls finish sooner. */
const SHOW_DELAY_MS = 250;
/** Once shown, stay up at least this long. */
const MIN_VISIBLE_MS = 400;

/**
 * Replaces the Zustand `useLoadingStore`.
 *
 * Ref-counted rather than a bare boolean. `useApiCall` guarded with
 * `if (!isShow) show()` but always called `hide()` in `finally`, so two
 * overlapping calls dismissed the loader early. React masked this with a
 * stale closure over `loadingIsShow`; a signal read is live, so a literal
 * port would surface the race.
 *
 * Visibility is also delayed and floored. A fast request used to flip
 * `isShow` true then false within a few hundred milliseconds, and Ionic's
 * `dismiss()` would land while `present()` was still in flight -- the
 * overlay stayed in the DOM, full-screen with `isOpen` false, silently
 * swallowing every tap on the page. Delaying the show means quick calls
 * never present at all, and the floor guarantees `present()` has finished
 * before anything dismisses it.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly _text = signal<string | null>(null);
  private readonly _depth = signal(0);
  private readonly _visible = signal(false);

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt = 0;

  readonly text = this._text.asReadonly();
  readonly isShow = computed(() => this._visible());

  /** Callers still pending, regardless of whether the overlay is on screen. */
  readonly isPending = computed(() => this._depth() > 0);

  show(text?: string): void {
    this._text.set(text ?? null);
    this._depth.update((d) => d + 1);

    if (this._visible() || this.showTimer) {
      return;
    }

    this.clearHideTimer();
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      if (this._depth() > 0) {
        this.shownAt = Date.now();
        this._visible.set(true);
      }
    }, SHOW_DELAY_MS);
  }

  hide(): void {
    this._depth.update((d) => Math.max(0, d - 1));
    if (this._depth() > 0) {
      return;
    }

    /* Finished before it ever appeared: cancel and show nothing. */
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
      this._text.set(null);
      return;
    }

    if (!this._visible()) {
      this._text.set(null);
      return;
    }

    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - this.shownAt));
    this.clearHideTimer();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this._depth() === 0) {
        this._visible.set(false);
        this._text.set(null);
      }
    }, remaining);
  }

  /**
   * Retargets the message of a loader already on screen.
   *
   * Deliberately does not touch the ref count -- calling show() again just to
   * change the text would need a matching hide(). The depth guard stops a
   * late callback from resurrecting a stale message after hide().
   */
  setText(text: string): void {
    if (this._depth() > 0) {
      this._text.set(text);
    }
  }

  /** Hard reset, for page transitions that should never inherit a loader. */
  reset(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    this._depth.set(0);
    this._visible.set(false);
    this._text.set(null);
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
